import React, { useState, useEffect } from 'react';
import { dashboardAPI, activityAPI, groupAPI, classAPI, gamificationAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import GroupChatDialog from './GroupChatDialog';
import DirectChatDialog from './DirectChatDialog';
import GroupDetailDialog from './GroupDetailDialog';
import ActivityFeed from './ActivityFeed';
import { Coins, Zap, Award, Target, TrendingUp, Calendar, CheckCircle2, Clock, Star, UsersRound, MessageCircle, MessageSquare, Crown, Wallet, ShoppingBag, Eye, XCircle, AlertTriangle, MessageSquareDashed, Send, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatCoins, formatDateTime } from '../../lib/utils';
import { stripHtml } from '../common/RichTextEditor';
import { XPBar } from '../common/XPBar';

export default function StudentDashboard({ classId, user, classData: classDataProp, openGroupChatId, onGroupChatOpened }) {
  const [data, setData] = useState(null);
  const [myGroups, setMyGroups] = useState([]);
  const [groupWallets, setGroupWallets] = useState({});
  const [loading, setLoading] = useState(true);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [directChatOpen, setDirectChatOpen] = useState(false);
  const [classDataLocal, setClassDataLocal] = useState(null);
  const [groupDetailOpen, setGroupDetailOpen] = useState(false);
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null);
  const [rejectionFeedback, setRejectionFeedback] = useState([]);
  const [resubmitting, setResubmitting] = useState(null);
  const [achievementsProgress, setAchievementsProgress] = useState(null);
  
  const classData = classDataProp || classDataLocal;

  useEffect(() => {
    loadDashboard();
  }, [classId]);

  // Fallback: fetch class data if not passed as prop
  useEffect(() => {
    if (!classDataProp && classId) {
      classAPI.getClass(classId).then(res => setClassDataLocal(res.data)).catch(() => {});
    }
  }, [classId, classDataProp]);

  // Auto-open group chat from notification
  useEffect(() => {
    if (openGroupChatId && myGroups.length > 0) {
      const group = myGroups.find(g => g.id === openGroupChatId);
      if (group) {
        handleOpenChat(group);
        onGroupChatOpened?.();
      }
    }
  }, [openGroupChatId, myGroups]);

  const loadDashboard = async () => {
    try {
      const [dashboardRes, groupsRes, rejectionsRes, achProgressRes] = await Promise.all([
        dashboardAPI.getStudentDashboard(classId),
        groupAPI.getMyGroups(classId),
        activityAPI.getMyRejectionFeedback(classId).catch(() => ({ data: [] })),
        gamificationAPI.getAchievementsProgress(classId).catch(() => ({ data: null }))
      ]);
      setData(dashboardRes.data);
      const groups = groupsRes.data || [];
      setMyGroups(groups);
      setRejectionFeedback(rejectionsRes.data || []);
      setAchievementsProgress(achProgressRes.data);
      
      // Load group wallets
      const wallets = {};
      for (const g of groups) {
        try {
          const wRes = await groupAPI.getGroupWallet(classId, g.id);
          wallets[g.id] = wRes.data;
        } catch { wallets[g.id] = { balance: 0 }; }
      }
      setGroupWallets(wallets);
    } catch (error) {
      console.error('StudentDashboard load error:', error);
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChat = (group) => {
    setSelectedGroup({
      ...group,
      members: [] // Will be loaded in chat
    });
    setChatDialogOpen(true);
  };

  const handleResubmit = async (activityId) => {
    setResubmitting(activityId);
    try {
      await activityAPI.updateStatus(classId, activityId, 'pending_approval');
      toast.success('Actividad reenviada para aprobación');
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar');
    } finally {
      setResubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const xpProgress = data.max_class_xp > 0 ? data.level_progress_pct : 0;

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      {/* Message Professor Button */}
      {classData?.professor_id && (
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-sm text-slate-900">Mensaje Directo</p>
                  <p className="text-xs text-slate-500">Chatea con {classData.professor_name || 'tu profesor'}</p>
                </div>
              </div>
              <Button
                onClick={() => setDirectChatOpen(true)}
                className="bg-indigo-500 hover:bg-indigo-600 h-9"
                data-testid="message-professor-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1.5" />
                Mensaje al Profesor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {/* My Groups Section - Top of Dashboard */}
      {myGroups.length > 0 && (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-slate-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <UsersRound className="w-5 h-5 text-blue-600" />
                Mis Grupos
              </CardTitle>
              <Badge variant="outline" className="text-blue-600 border-blue-200">
                {myGroups.length} {myGroups.length === 1 ? 'grupo' : 'grupos'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {myGroups.map((group) => (
                <div 
                  key={group.id}
                  className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all"
                  data-testid={`my-group-${group.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${group.color}15` }}
                  >
                    <UsersRound className="w-5 h-5" style={{ color: group.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-medium text-sm text-slate-900 truncate">{group.name}</h4>
                      {group.is_leader && (
                        <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {group.member_count} miembros
                    </p>
                    {groupWallets[group.id] && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Wallet className="w-3 h-3 text-amber-500" />
                        <span className="text-xs font-semibold text-amber-600">
                          {groupWallets[group.id].balance} coins
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedGroupDetail(group); setGroupDetailOpen(true); }}
                      className="h-7 text-xs border-slate-300"
                      data-testid={`group-detail-btn-${group.id}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      Detalles
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleOpenChat(group)}
                      className="bg-blue-500 hover:bg-blue-600 h-7 text-xs"
                      data-testid={`chat-btn-${group.id}`}
                    >
                      <MessageCircle className="w-3.5 h-3.5 mr-1" />
                      Chat
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hero Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {/* Balance Card */}
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100/50">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-amber-700 font-medium">Balance</p>
                <p className="text-2xl sm:text-4xl font-heading font-bold text-amber-800" data-testid="balance-display">
                  {formatCoins(data.balance)}
                </p>
                <p className="text-xs text-amber-600 mt-0.5">Coins disponibles</p>
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30 flex-shrink-0">
                <Coins className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Level Card */}
        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100/50">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div>
                <p className="text-xs sm:text-sm text-purple-700 font-medium">Nivel {data.level}</p>
                <p className="text-lg sm:text-2xl font-heading font-bold text-purple-800" data-testid="level-display">
                  {data.level_name || `Nivel ${data.level}`}
                </p>
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/30 flex-shrink-0">
                <Star className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-purple-600">
                <span>{data.total_xp} / {data.max_class_xp || '?'} XP</span>
                {data.next_level_name && <span>{data.xp_to_next_level} XP para {data.next_level_name}</span>}
              </div>
              <Progress value={xpProgress} className="h-2 bg-purple-200" />
              <p className="text-[10px] text-purple-500 text-right">{xpProgress}%</p>
            </div>
          </CardContent>
        </Card>

        {/* Activities Card */}
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100/50">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-emerald-700 font-medium">Actividades</p>
                <div className="flex items-baseline gap-1 sm:gap-2">
                  <p className="text-2xl sm:text-4xl font-heading font-bold text-emerald-800">
                    {data.completed_activities_count}
                  </p>
                  <p className="text-sm sm:text-lg text-emerald-600">completadas</p>
                </div>
                <p className="text-xs text-emerald-600 mt-0.5">{data.pending_activities_count} pendientes</p>
              </div>
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
            </div>
            {/* Activity progress bar */}
            {(data.completed_activities_count + data.pending_activities_count) > 0 && (
              <div className="mt-3 space-y-1">
                <Progress 
                  value={Math.round((data.completed_activities_count / (data.completed_activities_count + data.pending_activities_count)) * 100)} 
                  className="h-2 bg-emerald-200"
                />
                <p className="text-[10px] text-emerald-600 text-right" data-testid="activity-progress-pct">
                  {Math.round((data.completed_activities_count / (data.completed_activities_count + data.pending_activities_count)) * 100)}% completado
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* XP by Skills & Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Skills Progress */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              Habilidades
            </CardTitle>
            <CardDescription>Tu progreso en cada objetivo de aprendizaje</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.xp_by_objective.length > 0 ? (
              data.xp_by_objective.map((skill) => {
                const refXp = skill.max_xp || (skill.levels?.length ? Math.max(...skill.levels.map(l => l.xp_required)) : null);
                const pct = refXp ? Math.min(Math.round((skill.xp / refXp) * 100), 100) : Math.min(skill.xp, 100);
                return (
                  <div key={skill.learning_objective_id} className="space-y-1.5" data-testid={`skill-row-${skill.learning_objective_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-slate-700 truncate">{skill.learning_objective_name}</span>
                        {skill.current_level_name && (
                          <Badge
                            variant="outline"
                            className="text-[10px] h-5 px-1.5 flex-shrink-0"
                            style={{ borderColor: skill.learning_objective_color, color: skill.learning_objective_color }}
                          >
                            Nv.{skill.current_level} {skill.current_level_name}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-sm font-bold flex-shrink-0" style={{ color: skill.learning_objective_color }}>
                        <Zap className="w-4 h-4" />
                        {skill.xp}{skill.max_xp ? ` / ${skill.max_xp}` : ''} XP
                      </div>
                    </div>
                    <XPBar pct={pct} color={skill.learning_objective_color} />
                    <div className="flex justify-between text-[11px] text-slate-400">
                      <span>{pct}%</span>
                      {skill.next_level_name && skill.xp_for_next_level != null && (
                        <span>{skill.xp_for_next_level} XP para Nv.{skill.next_level} {skill.next_level_name}</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-slate-500 text-center py-4">
                Completa actividades para ganar XP en habilidades
              </p>
            )}
          </CardContent>
        </Card>

        {/* Achievements */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Logros
            </CardTitle>
            <CardDescription>
              {achievementsProgress
                ? `${achievementsProgress.earned_count} de ${achievementsProgress.total} desbloqueados`
                : 'Badges desbloqueados'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {achievementsProgress && achievementsProgress.achievements.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {achievementsProgress.achievements.map((ach) => (
                  <div
                    key={ach.achievement_id}
                    className={`flex flex-col items-center p-3 rounded-xl border transition-all ${
                      ach.earned
                        ? 'bg-amber-50/60 border-amber-200 hover:shadow-md'
                        : 'bg-slate-50 border-slate-100'
                    }`}
                    title={stripHtml(ach.description)}
                    data-testid={`achievement-tile-${ach.achievement_id}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${ach.earned ? '' : 'opacity-60'}`}
                      style={{ backgroundColor: ach.earned ? ach.badge_color + '20' : '#E2E8F0' }}
                    >
                      {ach.earned ? (
                        <Award className="w-6 h-6" style={{ color: ach.badge_color }} />
                      ) : (
                        <Lock className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <span className={`text-sm font-medium text-center ${ach.earned ? 'text-slate-800' : 'text-slate-500'}`}>
                      {ach.name}
                    </span>
                    {ach.earned ? (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 mt-1.5">
                        <CheckCircle2 className="w-3 h-3" /> Desbloqueado
                      </span>
                    ) : (
                      <div className="w-full mt-2 space-y-1">
                        <XPBar pct={ach.progress_pct} color={ach.badge_color} height="h-1.5" />
                        <div className="flex justify-between text-[10px] text-slate-400">
                          <span>{ach.progress_pct}%</span>
                          <span>{ach.current_xp}/{ach.required_xp} XP</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : data.achievements.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {data.achievements.map((ach) => (
                  <div 
                    key={ach.id} 
                    className="flex flex-col items-center p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    title={stripHtml(ach.achievement.description)}
                  >
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                      style={{ backgroundColor: ach.achievement.badge_color + '20' }}
                    >
                      <Award className="w-6 h-6" style={{ color: ach.achievement.badge_color }} />
                    </div>
                    <span className="text-sm font-medium text-slate-700 text-center">
                      {ach.achievement.name}
                    </span>
                    {ach.achievement.learning_objective_name && (
                      <span className="text-xs text-slate-500 mt-1">
                        {ach.achievement.learning_objective_name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aún no hay logros definidos</p>
                <p className="text-sm text-slate-400">Sigue completando actividades</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Retroalimentación del Profesor & Subscribed Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rejection Feedback */}
        <Card className={`border-slate-200 ${rejectionFeedback.length > 0 ? 'border-red-200' : ''}`} data-testid="rejection-feedback-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MessageSquareDashed className="w-5 h-5 text-red-500" />
              Retroalimentaci&oacute;n del Profesor
            </CardTitle>
            <CardDescription>Notas sobre entregas que necesitan ajustes</CardDescription>
          </CardHeader>
          <CardContent>
            {rejectionFeedback.length > 0 ? (
              <div className="space-y-3">
                {rejectionFeedback.map((item, idx) => (
                  <div 
                    key={`${item.activity_id}-${idx}`}
                    className="p-3 rounded-lg border border-red-200 bg-red-50/50"
                    data-testid={`rejection-item-${item.activity_id}`}
                  >
                    <div className="flex items-start gap-2">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-800">{item.activity_title}</p>
                        <div className="mt-1.5 p-2 bg-white rounded-md border border-red-100">
                          <p className="text-sm text-slate-700 italic">"{item.rejection_note}"</p>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <p className="text-[10px] text-slate-400">
                            {item.rejected_at ? formatDateTime(item.rejected_at) : ''}
                          </p>
                          {item.status === 'in_progress' && (
                            <Button
                              size="sm"
                              onClick={() => handleResubmit(item.activity_id)}
                              disabled={resubmitting === item.activity_id}
                              className="h-6 text-[11px] bg-blue-500 hover:bg-blue-600 px-2.5 ml-auto"
                              data-testid={`resubmit-btn-${item.activity_id}`}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              {resubmitting === item.activity_id ? 'Enviando...' : 'Reenviar'}
                            </Button>
                          )}
                          {item.status === 'completed' && (
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                              Completada
                            </Badge>
                          )}
                          {item.status === 'pending_approval' && (
                            <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">
                              Enviada
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                <p className="text-slate-500">Sin observaciones pendientes</p>
                <p className="text-sm text-slate-400">Todas tus entregas est&aacute;n en orden</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscribed Activities */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Mis Actividades
            </CardTitle>
            <CardDescription>Actividades a las que te has suscrito</CardDescription>
          </CardHeader>
          <CardContent>
            {data.subscribed_activities.length > 0 ? (
              <div className="space-y-3">
                {data.subscribed_activities.map((item) => {
                  const hasRejection = item.subscription.rejection_note;
                  const isCompleted = item.subscription.status === 'completed' || item.subscription.activity_status === 'completed';
                  const isPendingApproval = item.subscription.activity_status === 'pending_approval';
                  const showRejection = hasRejection && !isCompleted && !isPendingApproval;
                  return (
                    <div 
                      key={item.subscription.id}
                      className={`p-3 rounded-lg border ${
                        isCompleted
                          ? 'bg-emerald-50 border-emerald-200' 
                          : isPendingApproval
                            ? 'bg-blue-50/50 border-blue-200'
                            : showRejection
                              ? 'bg-red-50/50 border-red-200'
                              : 'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`subscribed-activity-${item.activity.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : isPendingApproval ? (
                            <Send className="w-5 h-5 text-blue-500" />
                          ) : showRejection ? (
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          ) : (
                            <Clock className="w-5 h-5 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium text-slate-700">{item.activity.title}</p>
                            <p className="text-xs text-slate-500">
                              {isCompleted ? 'Completada' : isPendingApproval ? 'Enviada - Esperando aprobación' : showRejection ? 'Rechazada - Revisa la nota' : 'Pendiente'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                            <Coins className="w-3 h-3 mr-1" />
                            {item.activity.coin_value}
                          </Badge>
                          {item.activity.xp_value > 0 && (
                            <Badge className="bg-purple-50 text-purple-700 border-purple-200">
                              <Zap className="w-3 h-3 mr-1" />
                              {item.activity.xp_value}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {showRejection && (
                        <div className="mt-2 ml-8 p-2 bg-white rounded-md border border-red-100">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="text-xs text-red-600 font-medium mb-0.5 flex items-center gap-1">
                                <MessageSquareDashed className="w-3 h-3" /> Nota del profesor:
                              </p>
                              <p className="text-xs text-slate-600 italic">"{item.subscription.rejection_note}"</p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleResubmit(item.activity.id)}
                              disabled={resubmitting === item.activity.id}
                              className="h-6 text-[11px] bg-blue-500 hover:bg-blue-600 px-2.5 flex-shrink-0"
                              data-testid={`resubmit-activity-btn-${item.activity.id}`}
                            >
                              <Send className="w-3 h-3 mr-1" />
                              {resubmitting === item.activity.id ? 'Enviando...' : 'Reenviar'}
                            </Button>
                          </div>
                        </div>
                      )}
                      {isPendingApproval && hasRejection && (
                        <div className="mt-2 ml-8 flex items-center gap-2 text-xs text-blue-600">
                          <Send className="w-3 h-3" />
                          <span>Reenviada - esperando revisi&oacute;n del profesor</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tienes actividades suscritas</p>
                <p className="text-sm text-slate-400">Ve a Actividades para suscribirte</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Transacciones Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_activities.length > 0 ? (
            <div className="space-y-3">
              {data.recent_activities.map((entry) => (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-700">{entry.note}</p>
                    <p className="text-xs text-slate-500">{formatDateTime(entry.created_at)}</p>
                  </div>
                  <span className={`font-heading font-bold ${
                    entry.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {entry.amount > 0 ? '+' : ''}{formatCoins(entry.amount)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Sin transacciones a&uacute;n</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Feed */}
      <ActivityFeed classId={classId} user={user} />

      {/* Group Chat Dialog */}
      <GroupChatDialog
        open={chatDialogOpen}
        onClose={() => setChatDialogOpen(false)}
        classId={classId}
        group={selectedGroup}
        currentUser={user}
        isProfessor={false}
      />

      {/* Direct Chat with Professor */}
      {classData?.professor_id && (
        <DirectChatDialog
          open={directChatOpen}
          onClose={() => setDirectChatOpen(false)}
          classId={classId}
          otherUser={{
            id: classData.professor_id,
            name: classData.professor_name,
            photo_url: classData.professor_photo_url,
            role: 'professor'
          }}
          currentUser={user}
        />
      )}

      {/* Group Detail Dialog (Student view) */}
      <GroupDetailDialog
        open={groupDetailOpen}
        onClose={() => { setGroupDetailOpen(false); setSelectedGroupDetail(null); }}
        classId={classId}
        groupId={selectedGroupDetail?.id}
      />
    </div>
  );
}
